import { parseCpf } from '@brazilian-utils/brazilian-utils';
import { instance } from '@payments-checkout/shared/helpers/service/instance.service';
import {
  DigitalWallets,
  DigitalWalletsStatusTypeAnaliticaEnum,
  StatusTypeAnaliticaEnum,
} from '@payments-checkout/shared/plugins/analitica/analitica.enum';
import { analiticaInstance } from '@payments-checkout/shared/plugins/analitica/analitica.events';
import { isInternalServerError } from '@payments-checkout/shared/helpers/constants/http-status.const';
import { isFailed } from '@payments-checkout/shared/helpers/constants/order-status.const';
import { reportDegradation, reportError } from '@payments-checkout/shared/observability';
import { usePaymentMethod } from '@payments-checkout/shared/providers';
import { useMutation } from '@tanstack/react-query';
import { useParams, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useFormTypedValues } from '../../app/providers/formValuesProvider.provider';
import { errorMessagePaymentGeneric } from '../../components/pages/cardAddress/errorMessage';
import { usePageSharedData } from '../../hooks/usePageSharedData';
import { usePayment } from '../../hooks/usePayment';
import { buildPaymentParams, isPaymentMethodAccepted } from '../helpers/clickToPayPayment';

type HSTEvent = 'cardSelected';

type HSTCardSelectedPayload = {
  cardType: 'CREDIT' | 'DEBIT';
};

declare global {
  interface Window {
    hst_Initialize: (data: any) => void;
    hst_Authorize: () => void;
    hst_SrcTrigger: (data: any) => void;
    onHSTEvent: (event: HSTEvent, callback: (payload: any) => void) => void;
  }
}
type ClickToPayParams = {
  credentials_payload: string;
  hst_sdk_url: string;
  csp_brands_urls: Array<string>;
  authentication_data?: any;
};

export async function sendClickToPaySession(paymentLinkId: string) {
  if (!paymentLinkId || paymentLinkId.length <= 0)
    throw new Error('Payment Link ID is required');

  const response = await instance.post(
    `paymentlinks/${paymentLinkId}/digital-wallets/click-to-pay/session`
  );

  return response.data;
}

export const useHSTCalls = () => {
  const [isInitialized, setIsInitialized] = useState(false);

  const initializeHST = (data: any) => {
    if (window.hst_Initialize) {
      window.hst_Initialize(data);
      setIsInitialized(true);
    }
  };

  const authorizeHST = () => {
    if (window.hst_Authorize) {
      window.hst_Authorize();
    }
  };

  const registerHSTEvent = (
    event: HSTEvent,
    callback: (payload: any) => void
  ) => {
    if (window.onHSTEvent) {
      window.onHSTEvent(event, callback);
    }
  };

  const triggerHst = (data: any) => {
    const documentType = data['documentType'];
    const nationalIdentifier = data[documentType];

    if (window.hst_SrcTrigger) {
      window.hst_SrcTrigger({
        nationalIdentifier: {
          value:
            documentType === 'cnpj'
              ? nationalIdentifier?.replace(/[^A-Z0-9]/gi, '').toUpperCase()
              : parseCpf(nationalIdentifier ?? ''),
          type: documentType,
        },
        email: data.email,
        mobileNumber: {
          countryCode: data['countryCode'],
          phoneNumber: data['phone'].replace(/\D/g, ''),
        },
        billingAddress: {
          name: data.name,
          line1: data['billing-address-street'],
          line2: data['billing-address-number'],
          line3: data['billing-address-complement'],
          city: data['billing-address-city'],
          state: data['billing-address-state'],
          zip: data['billing-address-zip-code'],
          countryCode: 'BR',
        },
        shippingAddress: {
          name: data.name,
          line1: data['billing-address-street'],
          line2: data['billing-address-number'],
          line3: data['billing-address-complement'],
          city: data['billing-address-city'],
          state: data['billing-address-state'],
          zip: data['billing-address-zip-code'],
          countryCode: 'BR',
        },
        fullName: data.name,
      });
    }
  };

  return {
    initializeHST,
    authorizeHST,
    triggerHst,
    registerHSTEvent,
    isInitialized,
  };
};

/**
 * Só o host: `hst_sdk_url` vem do BFF e a query pode carregar token. Vazio ou
 * malformado é o caso realista aqui — o `onerror` do script dispara justamente
 * quando a URL não presta.
 */
function sdkUrlHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return 'invalido';
  }
}

export const useClickToPay = () => {
  const {
    total: { amount },
    paymentLinkIdData,
  } = usePageSharedData();
  const { initializeHST, triggerHst, registerHSTEvent } = useHSTCalls();
  const params = useParams<{ paymentLinkId: string }>();
  const {
    setClickToPayData,
    clickToPayData,
    setClickToPayLoading,
    setFailedPaymentMessage,
    setShowFailedPaymentAlert,
    setShowClickToPay,
    setSubmitDisabled,
    setClickToPayCardType,
    installments,
  } = usePaymentMethod();
  const {
    mutate: sendPayment,
    isPending: isClickToPayPaymentPending,
    data: clickToPayPaymentResponse,
  } = usePayment({
    onError: (error) => {
      const errorStatus = error?.response?.status ?? 500;
      reportError(error, {
        source: 'payment_submit',
        payment_method: 'click_to_pay',
      });
      analiticaInstance.events.homePage.digitalWalletsPaymentStatus(
        StatusTypeAnaliticaEnum.ERROR,
        DigitalWallets.CLICK_TO_PAY
      );
      setShowFailedPaymentAlert(true);
      setFailedPaymentMessage(
        isInternalServerError(errorStatus)
          ? errorMessagePaymentGeneric(1002)
          : errorMessagePaymentGeneric(1000)
      );
    },
  });
  const { formValues } = useFormTypedValues();
  const { replace } = useRouter();

  const installmentsRef = useRef(installments);
  installmentsRef.current = installments;

  const {
    mutate: clickToPaySession,
    isPending: clickToPaySessionPending,
    isError: clickToPaySessionError,
  } = useMutation<ClickToPayParams>({
    mutationFn: () => sendClickToPaySession(params.paymentLinkId),
    onSuccess(data) {
      setClickToPayData(data);
      setClickToPayLoading(true);
      const script = document.createElement('script');
      script.src = data?.hst_sdk_url || '';
      script.async = true;

      script.onload = () => {
        setClickToPayLoading(false);
      };

      script.onerror = () => {
        reportDegradation(
          'digital_wallet_unavailable',
          new Error('Falha ao carregar o SDK da Click to Pay'),
          { wallet: 'click_to_pay', sdk_url_host: sdkUrlHost(script.src) }
        );
        setClickToPayLoading(false);
      };

      document.body.appendChild(script);
    },
  });

  function onHstSrcError() {
    setShowClickToPay(false);
    setShowFailedPaymentAlert(true);
    setFailedPaymentMessage(errorMessagePaymentGeneric(1006));
    analiticaInstance.events.homePage.digitalWalletsPaymentStatus(
      StatusTypeAnaliticaEnum.ERROR,
      DigitalWallets.CLICK_TO_PAY
    );
  }

  function onHstSrcCancel() {
    setClickToPayCardType(null);
    replace(`/${params.paymentLinkId}`);
    analiticaInstance.events.homePage.digitalWalletsPaymentStatus(
      DigitalWalletsStatusTypeAnaliticaEnum.DESISTENCIA,
      DigitalWallets.CLICK_TO_PAY
    );
  }

  function onHstSrcCheckout(checkoutData: any) {
    const acceptedPaymentMethods =
      paymentLinkIdData?.data?.payment_settings?.accepted_payment_methods ?? [];
    const cardType = (checkoutData?.selectedCardData?.cardType ?? 'CREDIT') as 'CREDIT' | 'DEBIT';

    setClickToPayCardType(checkoutData?.selectedCardData?.type ?? 'CREDIT');

    if (!isPaymentMethodAccepted(cardType, acceptedPaymentMethods)) {
      setFailedPaymentMessage(errorMessagePaymentGeneric(1000));
      setShowFailedPaymentAlert(true);
      return;
    }

    const paymentParams = buildPaymentParams(cardType, checkoutData, installmentsRef);
    sendPayment({ paymentData: paymentParams });
    setShowClickToPay(false);
  }

  useEffect(() => {
    if (!clickToPayPaymentResponse?.order_status) return;

    analiticaInstance.events.homePage.digitalWalletsPaymentStatus(
      isFailed(clickToPayPaymentResponse.order_status)
        ? StatusTypeAnaliticaEnum.ERROR
        : StatusTypeAnaliticaEnum.SUCCESS,
      DigitalWallets.CLICK_TO_PAY
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickToPayPaymentResponse?.order_status]);

  function togglePayButton(value: boolean) {
    setSubmitDisabled(!value);
  }

  function isAuthenticationAvailable() {
    return clickToPayData?.authentication_data
      ? 'AUTHENTICATED'
      : 'NOT_AUTHENTICATED';
  }

  function getAuthenticationData() {
    return {
      VISA: {
        acquirerBIN:
          clickToPayData?.authentication_data?.stone?.visa?.acquirer_bin
            ?.credit,
        acquirerMerchantId:
          clickToPayData?.authentication_data?.stone?.acquirer_merchant_id,
        merchantCategoryCode:
          clickToPayData?.authentication_data?.category_code,
      },
      MASTERCARD: {
        acquirerBIN:
          clickToPayData?.authentication_data?.stone?.mastercard?.acquirer_bin
            ?.credit,
        acquirerMerchantId:
          clickToPayData?.authentication_data?.stone?.acquirer_merchant_id,
        merchantCategoryCode:
          clickToPayData?.authentication_data?.category_code,
      },
    };
  }

  function notRecognizedCallback() {
    triggerHst(formValues);
  }

  function onCardSelected(payload: HSTCardSelectedPayload) {
    setClickToPayCardType(payload?.cardType ?? null);
  }

  const initializeClickToPay = () => {
    const isAuthenticated = isAuthenticationAvailable();
    const authenticationData = getAuthenticationData();

    registerHSTEvent('cardSelected', onCardSelected);

    initializeHST({
      credentialsPayload: clickToPayData?.credentials_payload,
      currencyCode: 'BRL',
      checkoutPurchaseValue: amount.toString(),
      locale: 'pt_BR',
      shippingAddressPreference: 'NONE',
      billingAddressPreference: 'NONE',
      checkoutCallback: onHstSrcCheckout,
      fatalErrorCallback: onHstSrcError,
      notRecognizedCallback: notRecognizedCallback,
      cancelCallback: onHstSrcCancel,
      forceActivation: ['VISA', 'MASTERCARD'],
      isCheckoutEnableCallback: togglePayButton,
      authenticationPreference: isAuthenticated,
      authenticationData: authenticationData,
    });
  };

  return {
    clickToPaySession,
    clickToPaySessionPending,
    clickToPaySessionError,
    clickToPayData,
    initializeClickToPay,
    isClickToPayPaymentPending,
    onHstSrcCancel,
  };
};
